/**
 * Reusable "nice" Y-axis scaling for Recharts.
 * Recharts' default Y-axis often picks awkward ticks (e.g. 0, 40, 80, 140).
 * This utility rounds the domain to human-friendly step sizes (10, 25, 50, …)
 * and returns explicit `domain` and `ticks` for `<YAxis domain={...} ticks={...} />`.
 *
 * **Zero-anchored** charts (`anchorAtZero` with no custom `domainMin`): Y-axis uses exactly
 * **four** evenly spaced ticks `{0, s, 2s, 3s}`. Domain ceiling (last tick) is ≥ **1.2×** the
 * data max (`tickCount` is not applied there).
 */

export type NiceYAxisScale = {
  domain: [number, number];
  ticks: number[];
};

/**
 * Infer decimal places from the distance between sequential axis ticks (`step`).
 * Exported for reuse with `<YAxis tickFormatter={…} />` — using `Math.round` alone collapses
 * every value in `(−½,½)` to `"0"`.
 */
export function fractionDigitsFromTickStep(step: number, maxDigits = 8): number {
  if (typeof step !== 'number' || !Number.isFinite(step) || step <= 0) {
    return 0;
  }
  if (step >= 1) {
    const r = Number(Number(step.toPrecision(12)));
    return Number.isInteger(r) ? 0 : Math.min(maxDigits, 8);
  }
  return Math.min(maxDigits, Math.max(1, Math.ceil(-Math.log10(step))));
}

/**
 * Formats a numeric Y-axis tick. Always pass `ticks` from {@link NiceYAxisScale} so decimals
 * match the arithmetic step (e.g. `0`, `0.1`, `0.2`, `0.3`).
 */
export function formatAxisTickValue(
  value: number,
  ticks: readonly number[]
): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '';
  const maxAbsTick =
    ticks.length > 0
      ? Math.max(...ticks.map((t) => Math.abs(Number(t))), 1e-300)
      : Math.abs(value);
  if (Math.abs(value) <= 1e-12 * Math.max(maxAbsTick, 1)) return '0';

  const uniq = [...new Set(ticks.map((x) => Number(x)))].sort((a, b) => a - b);
  const deltas =
    uniq.length >= 2
      ? uniq.slice(1).map((y, ix) => y - uniq[ix])
      : [];

  /** Smallest strictly positive pairwise gap ⇒ tick step when spacing is arithmetic. */
  const positiveDeltas = deltas.filter(
    (dt) => dt > 1e-15 * Math.max(maxAbsTick, Math.abs(uniq[uniq.length - 1] ?? 0))
  );
  let stepGuess = positiveDeltas.length
    ? Math.min(...positiveDeltas)
    : Number.NaN;
  /** Backstop for unsorted/non-arithmetic callers: first span from 0 next tick. */
  if ((!Number.isFinite(stepGuess) || stepGuess <= 0) && uniq.length >= 2) {
    stepGuess =
      uniq[0] <= 1e-12 * Math.max(Math.abs(maxAbsTick), 1)
        ? uniq[1]
        : Number.NaN;
  }

  const fracDigits = Number.isFinite(stepGuess) && stepGuess > 0
      ? fractionDigitsFromTickStep(stepGuess)
      : fractionDigitsFromTickStep(Math.min(maxAbsTick, Math.max(Number.EPSILON, maxAbsTick / 3)));

  const rounded = Number(value.toFixed(Math.min(8, fracDigits)));
  const useGrouping = maxAbsTick >= 10000;
  const out = Intl.NumberFormat('en-US', {
    useGrouping,
    minimumFractionDigits: 0,
    maximumFractionDigits: Math.min(8, fracDigits),
  }).format(Number.isFinite(rounded) ? rounded : value);
  return out;
}

export type GetNiceYAxisScaleOptions = {
  /**
   * When true (default), the axis floor is 0. Typical for bar charts.
   * Set false to allow a negative lower bound (e.g. some line charts).
   */
  anchorAtZero?: boolean;
  /**
   * Forces the lower bound of `domain[0]`. If set, overrides `anchorAtZero`
   * for the minimum (use with line charts: e.g. `minCO2e`).
   */
  domainMin?: number;
  /**
   * Inclusive tick count when `anchorAtZero` **and** custom `domainMin` diverge from the pure
   * anchored path, **or** for non‑anchored scales. Ignored when `anchorAtZero` is true without
   * `domainMin` — that path always emits **four** arithmetic ticks `{0, s, 2s, 3s}`.
   */
  tickCount?: number;
  /**
   * When false (default), domain and ticks never go below 0 (no negative Y ticks).
   * Set true only if the series can be negative and you need a negative axis.
   */
  allowNegativeTicks?: boolean;
};

const toFinite = (v: number | null | undefined): number | null => {
  if (typeof v !== 'number' || !Number.isFinite(v)) return null;
  return v;
};

const collectFinite = (data: (number | null | undefined)[]): number[] => {
  const out: number[] = [];
  for (const v of data) {
    const n = toFinite(v);
    if (n !== null) out.push(n);
  }
  return out;
};

/**
 * Given a positive rough step, returns a "nice" step (1, 2, 2.5, 5, 10… × 10ⁿ).
 */
function niceNumPositive(rough: number): number {
  if (rough <= 0) return 1;
  const exp = Math.floor(Math.log10(rough));
  const f = 10 ** exp;
  const x = rough / f;
  const nf: number =
    x <= 1
      ? 1
      : x <= 2
        ? 2
        : x <= 2.5
          ? 2.5
          : x <= 5
            ? 5
            : 10;
  return nf * f;
}

/** Intervals between first tick (0) and last tick: 4 ticks ⇒ 3 equal spans. */
const ZERO_ANCHOR_ARITHMETIC_SPANS = 3;

/** Minimum ratio: domain ceiling (last tick) must be ≥ rawMax × this (20% headroom). */
const MIN_DOM_CEILING_RATIO_TO_RAW_MAX = 1.2;

/** First tick fixed at 0; always four ticks `[0, s, 2s, 3s]`. Ceiling ≥ `rawMax × 1.2` post-`roundTick`. */
function anchoredZeroArithmeticNiceScale(rawMaxGreaterThanZero: number): NiceYAxisScale {
  const relTol = Math.max(1e-12 * rawMaxGreaterThanZero, 1e-15);
  const minCeilingRequired = rawMaxGreaterThanZero * MIN_DOM_CEILING_RATIO_TO_RAW_MAX;
  const floorTol = Math.max(
    relTol,
    1e-12 * minCeilingRequired,
    1e-15
  );

  let step = niceNumPositive(
    rawMaxGreaterThanZero / ZERO_ANCHOR_ARITHMETIC_SPANS + 1e-24
  );

  const buildTicks = (): { ticks: number[]; ceiling: number } => {
    const ticks: number[] = [
      roundTick(0),
      roundTick(step),
      roundTick(step * 2),
      roundTick(step * 3),
    ];
    const ceiling = ticks[ticks.length - 1];
    return { ticks, ceiling };
  };

  const meetsHeadroom = (c: number): boolean =>
    c > rawMaxGreaterThanZero + relTol && c + floorTol >= minCeilingRequired;

  let { ticks, ceiling } = buildTicks();

  /** Inflate step until apex is above data max AND ceiling ≥ rawMax × 1.2. */
  let inflate = 0;
  while (inflate < 96 && !meetsHeadroom(ceiling)) {
    step = niceNumPositive(step * (inflate < 72 ? 1.073 : 1.125));
    const next = buildTicks();
    ticks = next.ticks;
    ceiling = next.ceiling;
    inflate += 1;
  }

  if (!meetsHeadroom(ceiling)) {
    let widen = 0;
    while (!meetsHeadroom(ceiling) && widen < 24) {
      step = niceNumPositive(step * 2);
      const last = buildTicks();
      ticks = last.ticks;
      ceiling = last.ceiling;
      widen += 1;
    }
  }

  return {
    domain: [0, ceiling],
    ticks,
  };
}

/** Empty or all-null series → stable four-tick arithmetic scale starting at zero. */
function anchoredZeroEmptyOrAllZeros(): NiceYAxisScale {
  const step = 1;
  const ticks = [roundTick(0), step, step * 2, step * 3];
  return { domain: [0, step * ZERO_ANCHOR_ARITHMETIC_SPANS], ticks };
}

function buildTicksInRange(
  min: number,
  max: number,
  step: number
): number[] {
  const s = step > 0 ? step : 1;
  const start = Math.floor(min / s - 1e-12) * s;
  const end = Math.ceil(max / s + 1e-12) * s;
  const ticks: number[] = [];
  for (let t = start; t <= end + 1e-9; t += s) {
    ticks.push(roundTick(t));
  }
  return dedupeSortedTicks(ticks);
}

function roundTick(t: number): number {
  if (Math.abs(t) < 1e-9) return 0;
  return Number.parseFloat(t.toPrecision(12));
}

function dedupeSortedTicks(ticks: number[]): number[] {
  const out: number[] = [];
  for (const t of ticks) {
    if (out.length === 0 || Math.abs(t - out[out.length - 1]) > 1e-9) {
      out.push(t);
    }
  }
  return out;
}

/** Ensures no tick is below 0; keeps domain aligned with tick range. */
function withNonNegativeTicksOnly(scale: NiceYAxisScale): NiceYAxisScale {
  const ticks = dedupeSortedTicks(
    scale.ticks.map((t) => Math.max(0, roundTick(t))).sort((a, b) => a - b)
  );
  const lo = Math.max(0, scale.domain[0]);
  const hi = Math.max(lo, scale.domain[1], ticks[ticks.length - 1] ?? lo);
  if (ticks.length === 0) {
    return { domain: [0, Math.max(1, hi)], ticks: [0, Math.max(1, hi)] };
  }
  return {
    domain: [lo, hi],
    ticks,
  };
}

/**
 * Produces a nice Y scale from numeric samples (e.g. all bar values, or all line Y values).
 * Empty / all-zero data returns a small default range so the chart still renders.
 */
export function getNiceYAxisScale(
  data: (number | null | undefined)[],
  options: GetNiceYAxisScaleOptions = {}
): NiceYAxisScale {
  const clean = collectFinite(data);
  const tickCount = Math.max(2, options.tickCount ?? 4);
  const anchorAtZero = options.anchorAtZero !== false;
  const domainMinOpt = options.domainMin;
  const allowNegativeTicks = options.allowNegativeTicks === true;

  const rawMax = clean.length ? Math.max(...clean) : 0;
  const rawMin = clean.length ? Math.min(...clean) : 0;

  if (
    anchorAtZero &&
    domainMinOpt === undefined
  ) {
    if (
      clean.length === 0 ||
      (rawMin >= 0 && rawMax <= 0)
    ) {
      return anchoredZeroEmptyOrAllZeros();
    }
    if (rawMin >= 0 && rawMax > 0) {
      return anchoredZeroArithmeticNiceScale(rawMax);
    }
  }

  const gaps = Math.max(1, tickCount - 1);

  if (clean.length === 0) {
    return { domain: [0, 1], ticks: [0, 0.25, 0.5, 0.75, 1] };
  }

  if (rawMax === 0 && rawMin === 0) {
    return { domain: [0, 1], ticks: [0, 0.25, 0.5, 0.75, 1] };
  }

  let lo =
    domainMinOpt !== undefined
      ? domainMinOpt
      : anchorAtZero
        ? 0
        : Math.min(0, rawMin);
  if (!allowNegativeTicks) {
    lo = Math.max(0, lo);
  }
  let hi = Math.max(rawMax, lo);
  if (hi <= lo) {
    hi = lo + 1;
  }

  const span = hi - lo;
  const roughStep = span / gaps;
  const step = niceNumPositive(roughStep);
  const niceLo = Math.floor(lo / step + 1e-12) * step;
  let niceHi = Math.ceil(hi / step - 1e-12) * step;
  if (niceHi < hi) niceHi += step;
  const ticks = buildTicksInRange(
    Math.min(lo, niceLo),
    Math.max(hi, niceHi),
    step
  );
  if (ticks.length < 2) {
    const fallback: NiceYAxisScale = {
      domain: [niceLo, niceHi],
      ticks: [roundTick(niceLo), roundTick(niceHi)],
    };
    return allowNegativeTicks ? fallback : withNonNegativeTicksOnly(fallback);
  }
  const out: NiceYAxisScale = {
    domain: [ticks[0], ticks[ticks.length - 1]],
    ticks,
  };
  return allowNegativeTicks ? out : withNonNegativeTicksOnly(out);
}
